import { computed, DOCUMENT, Inject, inject, Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Nora from '@primeuix/themes/nora';
import { palette, updatePreset } from '@primeuix/themes';
import { Brand } from '../interfaces/application';
import { BehaviorSubject } from 'rxjs';

const presets = {
  Aura,
  Lara,
  Nora
} as const;

declare type KeyOfType<T> = keyof T extends infer U ? U : never;

export interface layoutConfig {
  preset?: string;
  primary?: string;
  surface?: string | undefined | null;
  darkTheme?: boolean;
  menuMode?: string;
}

declare type SurfacesType = {
  name?: string;
  palette?: {
    0?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    950?: string;
  };
};

declare type ThemeConfig = {
  name: Brand;
  primary: string;
  primaryHover: string;
  primaryActive: string;
  background: string;
  backgroundDark: string;
  backgroundDarkHover: string;
  backgroundDarkContrast: string;
}

@Injectable({
  providedIn: 'root'
})
export class LayoutService {

  private readonly _themes: ThemeConfig[] = [
    {
      name: Brand.LOGAME,
      primary: '#E03326',
      primaryHover: '#bf352a',
      primaryActive: '#a12d23',
      background: '#F6F6F6',
      backgroundDark: '#181923',
      backgroundDarkHover: '#11121c',
      backgroundDarkContrast: '#F6F6F6',
    },
    {
      name: Brand.B2XBET,
      primary: '#DFFF38',
      primaryHover: '#bad931',
      primaryActive: '#667523',
      background: '#F6F6F6',
      backgroundDark: '#1C2320',
      backgroundDarkHover: '#272d2a',
      backgroundDarkContrast: '#46553d',
    },
    {
      name: Brand.LIDERBET,
      primary: '#E03326',
      primaryHover: '#bf352a',
      primaryActive: '#a12d23',
      background: '#F6F6F6',
      backgroundDark: '#181923',
      backgroundDarkHover: '#11121c',
      backgroundDarkContrast: '#F6F6F6',
    },
    {
      name: Brand.GERALBET,
      primary: '#5763C9',
      primaryHover: '#424ca1',
      primaryActive: '#333b7d',
      background: '#F6F6F6',
      backgroundDark: '#1C1F26',
      backgroundDarkHover: '#13151c',
      backgroundDarkContrast: '#F6F6F6',
    },
    {
      name: Brand.GERALBETR2M,
      primary: '#5763C9',
      primaryHover: '#424ca1',
      primaryActive: '#333b7d',
      background: '#F6F6F6',
      backgroundDark: '#1C1F26',
      backgroundDarkHover: '#13151c',
      backgroundDarkContrast: '#F6F6F6',
    }
  ];

  private _layoutConfig: layoutConfig = {
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkTheme: false,
    menuMode: 'static'
  };

  private readonly _systemTitle: BehaviorSubject<string> = new BehaviorSubject<string>('Agent View');
  private readonly _showMenuDrawer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _selectedTheme: BehaviorSubject<ThemeConfig> = new BehaviorSubject<ThemeConfig>(this._themes[0]);

  private readonly _rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private readonly _document = inject(DOCUMENT);

  private readonly _renderer: Renderer2 = this._rendererFactory.createRenderer(null, null);
  private readonly _linkElement: HTMLLinkElement = this._document.querySelector('#favicon') as HTMLLinkElement;

  public changeFavicon(iconUrl: string): void {
    if (this._linkElement) {
      this._renderer.setAttribute(this._linkElement, 'href', iconUrl);
    }
  }

  public setSystemTitle(title: string): void {
    this._systemTitle.next(title);
  }

  public systemTitle$ = this._systemTitle.asObservable();

  public toggleMenuDrawer(state?: boolean): void {
    this._showMenuDrawer.next(state ?? !this._showMenuDrawer.value);
  }

  public showMenuDrawer$ = this._showMenuDrawer.asObservable();

  public setSelectedTheme(theme: ThemeConfig): void {
    this._selectedTheme.next(theme);
  }

  public selectedTheme$ = this._selectedTheme.asObservable();

  public layoutConfig = signal<layoutConfig>(this._layoutConfig);

  public selectedPrimaryColor = computed(() => {
    return this.layoutConfig().primary;
  });

  public palettes = computed<SurfacesType[]>(() => {
    const presetPalette = presets[this.layoutConfig().preset as KeyOfType<typeof presets>].primitive;
    const colors = ['emerald', 'green', 'lime', 'orange', 'amber', 'yellow', 'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple', 'fuchsia', 'pink', 'rose'];
    const palettes: SurfacesType[] = [{ name: 'noir', palette: {} }];

    colors.forEach((color) => {
      palettes.push({
        name: color,
        palette: presetPalette?.[color as KeyOfType<typeof presetPalette>] as SurfacesType['palette']
      });
    });

    return palettes;
  });

  getPresetExt(theme: ThemeConfig) {

    if (theme.name === Brand.B2XBET) {
      return {
        semantic: {
          primary: palette(theme.primary),
          colorScheme: {
            light: {
              primary: {
                color: '{primary.950}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.800}',
                activeColor: '{primary.700}'
              },
              highlight: {
                background: '{primary.950}',
                focusBackground: '{primary.700}',
                color: '#ffffff',
                focusColor: '#ffffff'
              }
            },
            dark: {
              primary: {
                color: '{primary.50}',
                contrastColor: '{primary.950}',
                hoverColor: '{primary.200}',
                activeColor: '{primary.300}'
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.300}',
                color: '{primary.950}',
                focusColor: '{primary.950}'
              }
            }
          }
        }
      };
    }

    if (theme.name === Brand.GERALBET || theme.name === Brand.GERALBETR2M) {
      return {
        semantic: {
          primary: palette(theme.primary),
          colorScheme: {
            light: {
              primary: {
                color: '{primary.950}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.800}',
                activeColor: '{primary.700}'
              },
              highlight: {
                background: '{primary.950}',
                focusBackground: '{primary.700}',
                color: '#ffffff',
                focusColor: '#ffffff'
              }
            },
            dark: {
              primary: {
                color: '{primary.50}',
                contrastColor: '{primary.950}',
                hoverColor: '{primary.200}',
                activeColor: '{primary.300}'
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.300}',
                color: '{primary.950}',
                focusColor: '{primary.950}'
              }
            }
          }
        }
      };
    }

    if (theme.name === Brand.LIDERBET) {
      return {
        semantic: {
          primary: palette(theme.primary),
          colorScheme: {
            light: {
              primary: {
                color: '{primary.950}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.800}',
                activeColor: '{primary.700}'
              },
              highlight: {
                background: '{primary.950}',
                focusBackground: '{primary.700}',
                color: '#ffffff',
                focusColor: '#ffffff'
              }
            },
            dark: {
              primary: {
                color: '{primary.50}',
                contrastColor: '{primary.950}',
                hoverColor: '{primary.200}',
                activeColor: '{primary.300}'
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.300}',
                color: '{primary.950}',
                focusColor: '{primary.950}'
              }
            }
          }
        }
      };
    }

    return {
      semantic: {
        primary: palette(theme.primary),
        colorScheme: {
          light: {
            primary: {
              color: '{primary.950}',
              contrastColor: '#ffffff',
              hoverColor: '{primary.800}',
              activeColor: '{primary.700}'
            },
            highlight: {
              background: '{primary.950}',
              focusBackground: '{primary.700}',
              color: '#ffffff',
              focusColor: '#ffffff'
            }
          },
          dark: {
            primary: {
              color: '{primary.50}',
              contrastColor: '{primary.950}',
              hoverColor: '{primary.200}',
              activeColor: '{primary.300}'
            },
            highlight: {
              background: '{primary.50}',
              focusBackground: '{primary.300}',
              color: '{primary.950}',
              focusColor: '{primary.950}'
            }
          }
        }
      }
    };
  }

  public initUpdateColors(themeName: Brand) {
    const theme = this._themes.find((t) => t.name === themeName);
    this.updatePrimeNgColors(theme!);
    this.updateTailwindcssColors(theme!);
  }

  public updatePrimeNgColors(theme: ThemeConfig) {
    this.setSelectedTheme(theme!);
    this.layoutConfig.update((state) => ({ ...state, primary: theme?.primary }));
    updatePreset(this.getPresetExt(theme!));
  }

  public updateTailwindcssColors(theme: ThemeConfig) {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme?.primary);
    root.style.setProperty('--color-primary-hover', theme?.primaryHover);
    root.style.setProperty('--color-primary-active', theme?.primaryActive);
    root.style.setProperty('--color-background', theme?.background);
    root.style.setProperty('--color-background-dark', theme?.backgroundDark);
    root.style.setProperty('--color-background-dark-hover', theme?.backgroundDarkHover);
    root.style.setProperty('--color-background-dark-constrast', theme?.backgroundDarkContrast);
  }

  public onPresetChange(event: any) {
    this.layoutConfig.update((state) => ({ ...state, preset: event }));
  }

  public onMenuModeChange(event: string) {
    this.layoutConfig.update((prev) => ({ ...prev, menuMode: event }));
  }
}
